import {
  Button,
  Card,
  Group,
  Loader,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconBug, IconCheck } from "@tabler/icons-react";
import {
  EventName,
  Frequency,
  getPlanChangeDirection,
  type PlanConfig,
  planLimits,
  SubscriptionType,
} from "core";
import { useState } from "react";
import { redirect, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../auth/useAuthContext";
import { useIsGranted } from "../../auth/useIsGranted";
import { SearchParam } from "../../utils/searchParams";
import { prefillCustomerPortalLink } from "../../utils/stripePortal";
import { trpc } from "../../utils/trpc";
import { useAccountBilling } from "../../utils/useAccount";
import { useAccountMutation } from "../../utils/useAccountMutation";
import { useTrackEvent } from "../../utils/useTrackEvent";
import { ChangePlanModal } from "./ChangePlanModal";
import classes from "./PricingModal.module.css";
import { ResubscribeModal } from "./ResubscribeModal";
import { groupPlansBySlug } from "./sortPlans";
import { usePromoCode } from "./usePromoCode";

const intervalLabel: Record<Frequency, string> = {
  [Frequency.Month]: "Monthly",
  [Frequency.Year]: "Yearly",
};

/**
 * Build a complete stripe pay link for a given email & plan:
 */
function buildSubscriptionUrl({
  paymentLink,
  email,
  accountId,
  promoCode = import.meta.env.DEV ? "NOANINVESTOR" : "",
}: {
  paymentLink: string | null;

  // prefilled_email
  email: string;

  // client_reference_id
  accountId: string;

  // prefilled_promo_code
  promoCode?: string;
}) {
  if (!paymentLink) return "";

  const baseUrl = new URL(paymentLink);
  baseUrl.search = new URLSearchParams({
    prefilled_email: email,
    client_reference_id: accountId,
    prefilled_promo_code: promoCode,
  }).toString();

  return baseUrl.toString();
}

function formatPrice(unitAmount: number | null) {
  if (unitAmount == null) return "";

  return `$${Math.floor(unitAmount / 100)}`;
}

/**
 * Marketing features are entered in Stripe as "Label\tValue", small formatting
 * here to display them as "Label - Value".
 */
function formatFeature(feature: string) {
  const [label, value] = feature.split("\t");

  return value ? `${label} - ${value}` : feature;
}

function FeatureList({ features }: { features: string[] }) {
  return (
    <Stack gap="xs" className={classes.featureList}>
      {features.map((feature) => (
        <Group key={feature} gap="xs" wrap="nowrap" align="flex-start">
          <ThemeIcon
            size={18}
            radius="xl"
            variant="light"
            color="orange"
            className={classes.featureIcon}
          >
            <IconCheck size={12} />
          </ThemeIcon>
          <Text size="sm" className={classes.featureText}>
            {formatFeature(feature)}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

const freeFeatures = [
  `Projects\t${planLimits.free.organizationLimit}`,
  `Facts\t${planLimits.free.factLimit}`,
  `Contacts\t${planLimits.free.contactLimit}`,
  `API keys\t${planLimits.free.apiKeyLimit}`,
  `Seats\t${planLimits.free.seatLimit}`,
];

function planButtonLabel({
  change,
  isCustomPlan,
  isCustomer,
  trialDays,
}: {
  change: string | null;
  isCustomPlan: boolean;
  isCustomer: boolean;
  trialDays: number | null;
}) {
  if (isCustomPlan) return "Custom plan";
  if (change) return change;
  if (isCustomer) return "Resume subscription";
  return trialDays ? "Start free trial" : "Subscribe";
}

/** Why the card's button is disabled, shown as its tooltip; null when it can be clicked. */
function blockedReason({
  change,
  isCustomPlan,
  isCustomer,
  hasBillingPermission,
  pendingBlocker,
}: {
  change: string | null;
  isCustomPlan: boolean;
  isCustomer: boolean;
  hasBillingPermission: boolean;
  pendingBlocker: string | null;
}) {
  if (isCustomPlan) {
    return "Your plan is managed by NOAN, contact support to change it";
  }
  if (change && pendingBlocker) return pendingBlocker;
  if (change && !hasBillingPermission) {
    return "Only the project owner can change the plan";
  }
  if (isCustomer && !hasBillingPermission) {
    return "Only the project owner can resume the subscription";
  }
  return null;
}

function changeLabel(current: PlanConfig, target: PlanConfig) {
  const direction = getPlanChangeDirection(current, target);
  if (direction === "current") return "Current plan";
  if (current.planSlug === target.planSlug) {
    return target.interval === Frequency.Year
      ? "Switch to yearly"
      : "Switch to monthly";
  }
  return direction === "upgrade" ? "Upgrade" : "Downgrade";
}

function PlanCardSkeleton() {
  return (
    <Card p="xl" className={classes.planCard}>
      <Stack gap="lg">
        <Stack gap="xs">
          <Skeleton height={24} width="60%" radius="sm" />
          <Skeleton height={40} width="45%" radius="sm" mt="xs" />
          <Skeleton height={14} width="90%" radius="sm" mt="xs" />
        </Stack>
        <Skeleton height={42} radius="xl" />
        <Stack gap="xs" mt="md">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} height={14} radius="sm" />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

export function Plans() {
  const [, setSearchParams] = useSearchParams();

  /** The modal is open as long as the param is there. */
  function closePricing() {
    setSearchParams(
      (params) => {
        params.delete(SearchParam.Upgrade);
        return params;
      },
      { replace: true },
    );
  }
  const [selectedPlan, setSelectedPlan] = useState<PlanConfig>();
  const [intervalByPlanSlug, setIntervalByPlanSlug] = useState<
    Record<string, Frequency>
  >({});
  const [
    isResubscribeOpened,
    { open: openResubscribe, close: closeResubscribe },
  ] = useDisclosure(false);
  const [isChangePlanOpened, { open: openChangePlan, close: closeChangePlan }] =
    useDisclosure(false);

  const { identity, isCustomer } = useAuthContext();
  const { hasPermission: hasBillingPermission } = useIsGranted({
    permission: "account.update.billing",
  });
  const { resubscribe, isResubscribing, chooseFreePlan, isChoosingFreePlan } =
    useAccountMutation();
  const {
    hasLiveSubscription,
    hasPaymentMethod,
    planSlug,
    interval,
    pendingChange,
    cancelAt,
    isFreePlan,
  } = useAccountBilling();
  const { promoCode } = usePromoCode();
  const { track } = useTrackEvent();
  const portalLink = prefillCustomerPortalLink({
    email: identity.email,
  });

  const { data: plans, isPending: isLoadingPlans } =
    trpc.account.plans.useQuery(undefined, {
      enabled: !!identity,
      select: groupPlansBySlug,
    });

  const promotionCode = promoCode;

  /** A live subscription with no matching plan is a custom one, changed from Stripe. */
  const currentPlan = hasLiveSubscription
    ? plans
        ?.flatMap((group) => group.variants)
        .find((v) => v.planSlug === planSlug && v.interval === interval)
    : undefined;
  const isCustomPlan = hasLiveSubscription && !!plans && !currentPlan;
  /** Stripe refuses a change preview while a schedule or cancellation is pending. */
  const pendingBlocker = pendingChange
    ? `Cancel the switch to ${pendingChange.planName} first, from Billing`
    : cancelAt
      ? "Keep your subscription first, from Billing"
      : null;
  const canResume = isCustomer && !hasLiveSubscription && hasBillingPermission;
  /** Free is only a choice for an account with nothing live. */
  const showFreeCard = !hasLiveSubscription && !isLoadingPlans;
  const freeBlocked = hasBillingPermission
    ? null
    : "Only the project owner can change the plan";

  function onChooseFree() {
    track(EventName.ClickedSubscriptionSelectPlanButton, { plan: "free" });
    chooseFreePlan(undefined, {
      onSuccess() {
        notifications.show({
          message: <Text>You are on the Free plan.</Text>,
          color: "green",
          icon: <IconCheck size={18} />,
        });
        closePricing();
      },
      onError() {
        notifications.show({
          message: (
            <Text>
              Could not switch you to the Free plan, please try again or contact
              customer support.
            </Text>
          ),
          color: "red",
          icon: <IconBug size={18} />,
        });
      },
    });
  }

  function onResubscribe() {
    if (!selectedPlan) return;

    const eventData = {
      plan: selectedPlan.planSlug,
      subscriptionType: SubscriptionType.Paid,
    };
    track(EventName.ClickedSubscriptionRenewalButton, eventData);

    resubscribe(
      {
        planSlug: selectedPlan.planSlug,
        interval: selectedPlan.interval,
      },
      {
        onSuccess() {
          track(EventName.RenewedSubscription, eventData);

          closeResubscribe();
          closePricing();
          notifications.show({
            message: <Text>Your subscription has been renewed!</Text>,
            color: "green",
            icon: <IconCheck size={18} />,
          });
        },
        onError() {
          notifications.show({
            message: (
              <Text>
                An error occurred while renewing your subscription. Make sure
                you have a payment method on file, or contact customer support.
              </Text>
            ),
            color: "red",
            icon: <IconBug size={18} />,
          });
        },
      },
    );
  }

  function onSelectPlan(plan: PlanConfig) {
    setSelectedPlan(plan);
    const eventData = {
      plan: plan.planSlug,
    };
    track(EventName.ClickedSubscriptionSelectPlanButton, eventData);

    /**
     * If users get this far without an account ready somehow, we
     * redirect them to join if they click one of the options:
     */
    if (!identity) {
      redirect("/join");
    }

    if (!isCustomer) {
      const url = buildSubscriptionUrl({
        paymentLink: plan.paymentLink,
        accountId: identity!.accountId,
        email: identity!.email,
        promoCode: promotionCode,
      });

      track(EventName.ViewedSubscriptionPaymentPage, {
        ...eventData,
        subscriptionType: SubscriptionType.Trial,
        paymentLink: url,
      });
      window.open(url, "_self");
    } else {
      track(EventName.ViewedSubscriptionRenewalModal, eventData);
      openResubscribe();
    }
  }

  const hasAnyMultiIntervalPlan =
    plans?.some((group) => group.variants.length > 1) ?? false;

  return (
    <Stack gap="xl">
      <Group justify="center" align="stretch" gap="lg">
        {showFreeCard && (
          <Card p="xl" className={classes.planCard}>
            <Stack h="100%" gap="lg">
              <Stack gap="xs">
                <Text className={classes.planName}>Free</Text>
                <Group gap={6} align="baseline" className={classes.priceRow}>
                  <Text className={classes.priceAmount}>$0</Text>
                  <Text size="sm" className={classes.priceFrequency}>
                    /month
                  </Text>
                </Group>
                {hasAnyMultiIntervalPlan && (
                  <SegmentedControl
                    size="xs"
                    className={classes.intervalTogglePlaceholder}
                    value={Frequency.Month}
                    data={[
                      {
                        label: intervalLabel[Frequency.Month],
                        value: Frequency.Month,
                      },
                    ]}
                  />
                )}
                <Text size="sm" className={classes.planDescription}>
                  One project to keep your facts and contacts in one place.
                </Text>
              </Stack>

              <Tooltip label={freeBlocked} disabled={!freeBlocked}>
                <Button
                  w="100%"
                  size="md"
                  px="xl"
                  radius="xl"
                  variant="light"
                  onClick={onChooseFree}
                  disabled={isFreePlan || !!freeBlocked || isChoosingFreePlan}
                  loading={isChoosingFreePlan}
                  loaderProps={{
                    children: <Loader size={18} />,
                  }}
                >
                  {isFreePlan ? "Current plan" : "Continue on Free"}
                </Button>
              </Tooltip>

              <FeatureList features={freeFeatures} />
            </Stack>
          </Card>
        )}
        {isLoadingPlans &&
          Array.from({ length: 2 }).map((_, index) => (
            <PlanCardSkeleton key={index} />
          ))}
        {plans?.map((group) => {
          const activeInterval =
            intervalByPlanSlug[group.planSlug] ?? group.variants[0].interval;
          const p =
            group.variants.find((v) => v.interval === activeInterval) ??
            group.variants[0];
          const priceFull = formatPrice(p.baseUnitAmount);
          const features = p.marketingFeatures;
          const change = currentPlan ? changeLabel(currentPlan, p) : null;
          const blocked = blockedReason({
            change,
            isCustomPlan,
            isCustomer,
            hasBillingPermission,
            pendingBlocker,
          });

          return (
            <Card key={group.planSlug} p="xl" className={classes.planCard}>
              <Stack h="100%" gap="lg">
                <Stack gap="xs">
                  <Text className={classes.planName}>{p.name}</Text>
                  <Group gap={6} align="baseline" className={classes.priceRow}>
                    <Text className={classes.priceAmount}>{priceFull}</Text>
                    <Text size="sm" className={classes.priceFrequency}>
                      /{p.interval}
                    </Text>
                  </Group>
                  {hasAnyMultiIntervalPlan && (
                    <SegmentedControl
                      size="xs"
                      className={
                        group.variants.length > 1
                          ? undefined
                          : classes.intervalTogglePlaceholder
                      }
                      value={activeInterval}
                      onChange={(value) =>
                        setIntervalByPlanSlug((prev) => ({
                          ...prev,
                          [group.planSlug]: value as Frequency,
                        }))
                      }
                      data={
                        group.variants.length > 1
                          ? group.variants.map((v) => ({
                              label: intervalLabel[v.interval],
                              value: v.interval,
                            }))
                          : [
                              {
                                label: intervalLabel[p.interval],
                                value: p.interval,
                              },
                            ]
                      }
                    />
                  )}
                  {p.description && (
                    <Text size="sm" className={classes.planDescription}>
                      {p.description}
                    </Text>
                  )}
                </Stack>

                <Tooltip label={blocked} disabled={!blocked}>
                  <Button
                    w="100%"
                    size="md"
                    px="xl"
                    radius="xl"
                    onClick={() => {
                      if (!change) return onSelectPlan(p);
                      setSelectedPlan(p);
                      openChangePlan();
                    }}
                    disabled={
                      isResubscribing || !!blocked || change === "Current plan"
                    }
                    loading={isResubscribing}
                    loaderProps={{
                      children: <Loader size={18} />,
                    }}
                  >
                    {planButtonLabel({
                      change,
                      isCustomPlan,
                      isCustomer,
                      trialDays: p.trialDays,
                    })}
                  </Button>
                </Tooltip>

                <FeatureList features={features} />
              </Stack>
            </Card>
          );
        })}
      </Group>
      {canResume && !hasPaymentMethod && (
        <Group justify="center" gap="xs" wrap="wrap">
          <Button
            component="a"
            size="sm"
            px="xl"
            href={portalLink}
            target="_blank"
            referrerPolicy="no-referrer"
            variant="light"
            color="yellow"
          >
            Add payment method
          </Button>
          <Text size="xs" c="dimmed">
            Make sure you have a payment method on file before resuming.
          </Text>
        </Group>
      )}
      <ResubscribeModal
        plan={selectedPlan}
        onConfirm={onResubscribe}
        isLoading={isResubscribing}
        opened={isResubscribeOpened}
        close={closeResubscribe}
      />
      <ChangePlanModal
        plan={selectedPlan}
        currentPlanName={currentPlan?.name ?? null}
        confirmLabel={
          currentPlan && selectedPlan
            ? changeLabel(currentPlan, selectedPlan)
            : undefined
        }
        opened={isChangePlanOpened}
        onClose={closeChangePlan}
      />
    </Stack>
  );
}
